import { useEffect, useRef, useCallback } from 'react';

// Throttle function to limit how often a function can be called
function throttle<T extends (...args: never[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return function (this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

interface UseIdleTimeoutOptions {
  /** Timeout duration in milliseconds before logout is triggered */
  timeout: number;
  /** Callback function to execute when timeout is reached */
  onTimeout: () => void;
  /** Whether the idle timeout is enabled */
  enabled?: boolean;
  /** Throttle duration for activity events in milliseconds */
  throttle?: number;
}

/**
 * Hook to automatically logout user after a period of inactivity
 *
 * Monitors user activity (mouse, keyboard, touch, scroll) and triggers
 * logout callback after specified timeout with no activity.
 *
 * @param options - Configuration options for idle timeout
 * @returns void
 *
 * @example
 * ```tsx
 * useIdleTimeout({
 *   timeout: 30 * 60 * 1000, // 30 minutes
 *   onTimeout: () => logout(),
 *   enabled: isAuthenticated,
 * });
 * ```
 */
export function useIdleTimeout({
  timeout,
  onTimeout,
  enabled = true,
  throttle: throttleDuration = 1000,
}: UseIdleTimeoutOptions): void {
  const lastActivityRef = useRef<number>(Date.now());
  const timeoutCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update last activity timestamp (throttled)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateActivity = useCallback(
    throttle(() => {
      lastActivityRef.current = Date.now();
    }, throttleDuration),
    [throttleDuration]
  );

  // Check if timeout has been exceeded
  const checkTimeout = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity >= timeout) {
      onTimeout();
    }
  }, [timeout, onTimeout]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Activity events to monitor
    const events: (keyof DocumentEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ];

    // Add event listeners with passive option for better performance
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    // Check for timeout every minute
    timeoutCheckIntervalRef.current = setInterval(checkTimeout, 60000);

    // Cleanup on unmount or when disabled
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity);
      });

      if (timeoutCheckIntervalRef.current) {
        clearInterval(timeoutCheckIntervalRef.current);
      }
    };
  }, [enabled, updateActivity, checkTimeout]);
}
